import React, { useState, useEffect, useRef } from 'react';
import styles from './AdminModals.module.css';
import { User } from '../../types';
import { Modal } from '../../components/common/Modal/Modal';
import { Button } from '../../components/common/Button/Button';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI, getApiBaseUrl } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

interface UserManagementModalProps {
  user: User | null;
  companies: any[];
  projects?: any[];
  onSave: (userData: any) => void;
  onClose: () => void;
}

export const UserManagementModal: React.FC<UserManagementModalProps> = ({
  user,
  companies,
  projects = [],
  onSave,
  onClose
}) => {
  const { isSuperAdmin } = useAuth();
  const toast = useToast();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'viewer',
    company: '',
    isActive: true,
    projects: [] as string[],
  });

  useEffect(() => {
    if (user) {
      const names = user.name?.split(' ') || [];
      setFormData({
        firstName: (user as any).firstName || names[0] || '',
        lastName: (user as any).lastName || names.slice(1).join(' ') || '',
        email: user.email,
        password: '',
        role: user.role || 'viewer',
        company: (user as any).company?._id || (user as any).company || '',
        isActive: (user as any).isActive !== false,
        projects: ((user as any).projects || []).map((p: any) => p._id || p),
      });
      // Set avatar preview
      const av = (user as any).avatar;
      if (av) {
        if (av.startsWith('http')) {
          setAvatarPreview(av);
        } else {
          const origin = getApiBaseUrl().replace(/\/api$/, '');
          setAvatarPreview(`${origin}${av}`);
        }
      } else {
        setAvatarPreview(null);
      }
    }
  }, [user]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bestand te groot', 'Maximaal 5 MB.');
      return;
    }
    setUploadingAvatar(true);
    try {
      const avatarUrl = await usersAPI.uploadAvatar(user.id, file);
      const origin = getApiBaseUrl().replace(/\/api$/, '');
      setAvatarPreview(avatarUrl.startsWith('http') ? avatarUrl : `${origin}${avatarUrl}`);
      toast.success('Profielfoto bijgewerkt');
    } catch (err: any) {
      toast.error('Upload mislukt', err.response?.data?.error || 'Er ging iets mis.');
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const filteredProjects = projects;

  const handleProjectToggle = (projectId: string) => {
    setFormData(prev => ({
      ...prev,
      projects: prev.projects.includes(projectId)
        ? prev.projects.filter(id => id !== projectId)
        : [...prev.projects, projectId],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const userData: any = { ...formData };
    if (!user && !userData.password) {
      // For new users, password is required
      return;
    }
    if (user && !userData.password) {
      // For existing users, don't send password if not changed
      delete userData.password;
    }
    onSave(userData);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={user ? 'Edit User' : 'Create New User'}>
      <form onSubmit={handleSubmit} className={styles.form}>
        {user && (
          <div className={styles.formGroup} style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: '#E6007E',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.25rem', fontWeight: 600, overflow: 'hidden', flexShrink: 0,
            }}>
              {avatarPreview ? (
                <img src={avatarPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                (formData.firstName?.[0] || '') + (formData.lastName?.[0] || '') || 'U'
              )}
            </div>
            <div>
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                style={{
                  fontSize: '0.8125rem', fontWeight: 600, padding: '0.375rem 0.75rem',
                  border: '1px solid #DDD', borderRadius: 6, background: '#fff',
                  cursor: 'pointer', color: '#323232',
                }}
              >
                {uploadingAvatar ? 'Uploaden...' : 'Foto wijzigen'}
              </button>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleAvatarUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        )}

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="firstName">First Name *</label>
            <input
              type="text"
              id="firstName"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="lastName">Last Name *</label>
            <input
              type="text"
              id="lastName"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>

        {!user && (
          <div className={styles.formGroup}>
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!user}
              minLength={6}
            />
            <small>Minimum 6 characters</small>
          </div>
        )}

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="role">Role *</label>
            <select
              id="role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
            >
              <option value="viewer">Viewer</option>
              <option value="stakeholder">Stakeholder</option>
              <option value="projectManager">Project Manager</option>
              <option value="admin">Admin</option>
              {isSuperAdmin && <option value="superAdmin">Super Admin</option>}
            </select>
          </div>

          {isSuperAdmin && (
            <div className={styles.formGroup}>
              <label htmlFor="company">Company</label>
              <select
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              >
                <option value="">Select Company</option>
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label>Projecten toewijzen</label>
          {filteredProjects.length > 0 ? (
            <>
              <div style={{
                maxHeight: '200px', overflowY: 'auto', border: '1px solid #DDDDDD',
                borderRadius: '6px', padding: '0.5rem',
              }}>
                {filteredProjects.map((project: any) => {
                  const projectId = project._id || project.id;
                  const location = typeof project.location === 'object'
                    ? project.location?.city || ''
                    : project.location || '';
                  return (
                    <label
                      key={projectId}
                      className={styles.checkboxLabel}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.375rem 0.25rem', cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.projects.includes(projectId)}
                        onChange={() => handleProjectToggle(projectId)}
                        style={{ accentColor: '#E6007E' }}
                      />
                      <span style={{ flex: 1 }}>
                        {project.name}
                        {location && <span style={{ color: '#9DA6A8', marginLeft: '0.5rem', fontSize: '0.85em' }}>({location})</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
              <small>{formData.projects.length} project(en) geselecteerd</small>
            </>
          ) : (
            <small style={{ color: '#9DA6A8' }}>Geen projecten beschikbaar</small>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
            <span>Actieve gebruiker</span>
          </label>
        </div>

        <div className={styles.formActions}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            {user ? 'Update User' : 'Create User'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};